import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Button, 
  Paper,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useGlobalContext } from '../context/GlobalProvider';
import HijosData from './data/HijosData';
import GiraInfData from './data/GiraInfData';
import GiraInfIns from './data/GiraInfIns';
import ChildRegistrationModal from './modals/ChildRegistrationModal';
import GiraInfRegistration from './modals/GiraInfRegistration';
import ClubesData from './data/ClubesData';
import JustificanteEscolar from './generatePDF/justificanteEscolar';
import FichaDeposito from './generatePDF/fichaDeposito';
import CredencialGira from './generatePDF/credencialGira';
import CircularProgress from '@mui/material/CircularProgress';
import { agsRoles, incrementEventInsForEtapaInfantil } from '../lib/appwrite';
import { API_BASE_URL } from '../config/api';

const Infantiles = ({
  clubes: clubesProp = [],
  giras: girasProp = [],
  registeredChildren: childrenProp = [],
  onUpdate,
}) => {
  const { user } = useGlobalContext();
  const [registeredChildren, setRegisteredChildren] = useState(
    Array.isArray(childrenProp) && childrenProp.length > 0 ? childrenProp : []
  );
  const [selectedChild, setSelectedChild] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openGiraModal, setOpenGiraModal] = useState(false);
  const [clubes, setClubes] = useState(
    Array.isArray(clubesProp) && clubesProp.length > 0 ? clubesProp : []
  );
  const [giras, setGiras] = useState(
    Array.isArray(girasProp) && girasProp.length > 0 ? girasProp : []
  );
  const [currentRegistrations, setCurrentRegistrations] = useState({});
  //Variables para registro de inscripcion
  const [userPadcodigo, setUserPadcodigo] = useState(null); //Codigo del Padre inspadre
  const [selectedGira, setSelectedGira] = useState(
    Array.isArray(girasProp) && girasProp.length > 0 ? girasProp[0] : null
  ); //Codigo de gira insgira = selectedGira.gira
  const [userData, setUserData] = useState(null); // Store user data
  const [bancoLectura, setBancoLectura] = useState(null);
  
  // Loading states
  const [isLoadingGiras, setIsLoadingGiras] = useState(
    !(Array.isArray(girasProp) && girasProp.length > 0)
  );
  const [isLoadingClubes, setIsLoadingClubes] = useState(
    !(Array.isArray(clubesProp) && clubesProp.length > 0)
  );
  const [isLoadingChildren, setIsLoadingChildren] = useState(
    !(Array.isArray(childrenProp) && childrenProp.length > 0)
  );

  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success' // 'success' | 'error' | 'info' | 'warning'
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    child: null
  });
  const [generatingJustificante, setGeneratingJustificante] = useState(false);
  const [generatingFicha, setGeneratingFicha] = useState(false);
  const [generatingCredencial, setGeneratingCredencial] = useState(false);
  const [isSavingInscription, setIsSavingInscription] = useState(false);

  const fetchUserData = useCallback(async () => {
    if (user?.email) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/profile?email=${user.email}`);
        const data = await response.json();
        if (data.Padre && data.Padre.length > 0) {
          setUserPadcodigo(data.Padre[0].padcodigo);
          setUserData(data.Padre[0]);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    }
  }, [user?.email]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '—';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Function to parse dates as local midnight to prevent timezone shifts
  const parseAsLocalDate = (dateStr) => {
    const [year, month, day] = dateStr.split("T")[0].split("-");
    return new Date(year, month - 1, day); // local midnight
  };

  // Extrae YYYY-MM-DD de ISO, SQL datetime o Date
  const getDateYmdFromValue = (value) => {
    if (!value) return null;
    const match = String(value).match(/(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;
    return { y: Number(match[1]), m: Number(match[2]), d: Number(match[3]) };
  };

  const getMexicoTodayYmd = () => {
    const formatted = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Mexico_City',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
    const [y, m, d] = formatted.split('-').map(Number);
    return { y, m, d };
  };

  const isYmdAfter = (a, b) => (
    a.y !== b.y ? a.y > b.y : a.m !== b.m ? a.m > b.m : a.d > b.d
  );

  // Tolerancia de 1 día: si girafininscripcion es el 4, se cierra a partir del 5
  const isInscriptionClosed = (insfin) => {
    const deadline = getDateYmdFromValue(insfin);
    if (!deadline) return false;
    return isYmdAfter(getMexicoTodayYmd(), deadline);
  };

  const defineEtapas = (etapas, registration = null) => {
    // Check if any child in the family has WAGR status
    const hasFamilyWAGR = currentRegistrations && currentRegistrations.some(reg => reg.insimportewagr > 0);
    
    // If it's an eventual registration (instipo = 3), only include etapas after the registration date
    if (registration && registration.instipo === 3 && registration.insfecha) {
      const registrationDate = new Date(registration.insfecha);
      console.log('Eventual registration detected:', {
        instipo: registration.instipo,
        insfecha: registration.insfecha,
        registrationDate: registrationDate.toISOString()
      });
      
      // Filter etapas that start after the registration date
      const futureEtapas = etapas.filter(etapa => {
        const etapaStart = new Date(etapa.etafinicio);
        return etapaStart > registrationDate;
      });
      
      console.log('Future etapas after registration date:', futureEtapas.length);
      
      // Only include the next etapa by date
      if (futureEtapas.length > 0) {
        // Sort by start date and take only the first one
        const nextEtapa = futureEtapas.sort((a, b) => 
          new Date(a.etafinicio) - new Date(b.etafinicio)
        )[0];
        
        console.log('Next etapa selected:', nextEtapa.etanombre);
        
        // Parse dates as local midnight to prevent timezone shifts
        const inicio = parseAsLocalDate(nextEtapa.etafinicio);
        const fin = parseAsLocalDate(nextEtapa.etaffina);
        
        // Use family WAGR status to determine date adjustment
        const daysToSubtract = hasFamilyWAGR ? 2 : 1;
        
        // Subtract days from inicio based on family WAGR status
        inicio.setDate(inicio.getDate() - daysToSubtract);
        // Add 1 day to fin
        fin.setDate(fin.getDate() + 1);
        
        return [`${nextEtapa.etanombre} (${formatDate(inicio)} a ${formatDate(fin)})`];
      }
      
      console.log('No future etapas found, returning empty array');
      // If no future etapas, return empty array
      return [];
    }
    
    // For regular registrations, include all etapas as before
    console.log('Regular registration, including all etapas:', etapas.length);
    const etapasArray = etapas.map(etapa => {
      const inicio = parseAsLocalDate(etapa.etafinicio);
      const fin = parseAsLocalDate(etapa.etaffina);
      
      // Use family WAGR status to determine date adjustment instead of individual child WAGR
      const daysToSubtract = hasFamilyWAGR ? 2 : 1;
      
      // Subtract days from inicio based on family WAGR status
      inicio.setDate(inicio.getDate() - daysToSubtract);
      fin.setDate(fin.getDate() + 1);

      return `${etapa.etanombre} (${formatDate(inicio)} a ${formatDate(fin)})`;
    });
    return etapasArray;
  };

  //Fetch the Club table from the server
  useEffect(() => {
    if (Array.isArray(clubesProp) && clubesProp.length > 0) {
      setClubes(clubesProp);
      setIsLoadingClubes(false);
      return;
    }

    const fetchClubes = async () => {
      setIsLoadingClubes(true);
      try {
        const clubesData = await ClubesData.fetch();
        if (clubesData.message) {
          console.error('Error loading clubes:', clubesData.message);
          return;
        }
        setClubes(clubesData);
      } catch (error) {
        console.error('Error loading clubes:', error);
      } finally {
        setIsLoadingClubes(false);
      }
    };

    fetchClubes();
  }, [clubesProp]);

  //Fetch the current Gira Infantil from the API
  useEffect(() => {
    if (Array.isArray(girasProp) && girasProp.length > 0) {
      setGiras(girasProp);
      setSelectedGira((prev) => prev || girasProp[0]);
      setIsLoadingGiras(false);
      return;
    }

    let isMounted = true;

    const fetchGiras = async () => {
      setIsLoadingGiras(true);
      try {
        const girasData = await GiraInfData.fetch();
        if (isMounted) {
          if (Array.isArray(girasData) && girasData.length > 0) {
            setGiras(girasData);
            setSelectedGira(girasData[0]);
          } else {
            setGiras([]);
            setSelectedGira(null);
          }
        }
      } catch (error) {
        console.error('Error fetching giras data:', error);
      } finally {
        if (isMounted) {
          setIsLoadingGiras(false);
        }
      }
    };

    fetchGiras();

    return () => {
      isMounted = false;
    };
  }, [girasProp]);

  const calculateAge = (birthDate, tournamentDate = null) => {
    const referenceDate = tournamentDate ? new Date(tournamentDate) : new Date();
    const birth = new Date(birthDate);
    let age = referenceDate.getFullYear() - birth.getFullYear();
    const monthDiff = referenceDate.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && referenceDate.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  };

  const getCategoria = (rama) => {
    return rama === 'V' ? 'Varonil' : 'Femenil';
  };

  // Próxima etapa vigente (misma lógica que al guardar eventual)
  const getNextUpcomingEtapaCodigo = (etapas) => {
    if (!etapas || !Array.isArray(etapas) || etapas.length === 0) return 0;
    const fecha = new Date();
    const sortedEtapas = [...etapas].sort((a, b) =>
      new Date(a.etafinicio) - new Date(b.etafinicio)
    );
    const nextEtapa = sortedEtapas.find(etapa =>
      new Date(etapa.etafinicio) > fecha
    );
    const fallback = sortedEtapas[sortedEtapas.length - 1];
    const etapa = nextEtapa || fallback;
    return etapa?.etacodigo ?? etapa?.Etacodigo ?? 0;
  };

  // Bloquea si tiene gira completa (insetapa 0) o ya está inscrito en la etapa vigente
  const isPlayerRegistered = (giraCode, playerHicodigo, etapas) => {
    if (!currentRegistrations || !Array.isArray(currentRegistrations)) return false;
    const vigenteEtapa = getNextUpcomingEtapaCodigo(etapas);
    return currentRegistrations.some(reg =>
      Number(reg.insgira) === Number(giraCode) &&
      Number(reg.inshijo) === Number(playerHicodigo) &&
      (Number(reg.insetapa) === 0 || Number(reg.insetapa) === Number(vigenteEtapa))
    );
  };

  // Check if all players are already registered for a specific tournament
  const areAllPlayersRegistered = (giraCode, etapas) => {
    if (!registeredChildren || !Array.isArray(registeredChildren)) return false;
    return registeredChildren.every(child =>
      isPlayerRegistered(giraCode, child.hicodigo, etapas)
    );
  };

  //Fetch the users children from the API
  useEffect(() => {
    if (Array.isArray(childrenProp) && childrenProp.length > 0) {
      setRegisteredChildren(childrenProp);
      setIsLoadingChildren(false);
      return;
    }

    const fetchChildren = async () => {
      if (user?.email) {
        setIsLoadingChildren(true);
        try {
          const childrenData = await HijosData.fetch(user.email);
          setRegisteredChildren(childrenData);
        } catch (error) {
          console.error('Error fetching children data:', error);
        } finally {
          setIsLoadingChildren(false);
        }
      } else {
        setIsLoadingChildren(false);
      }
    };

    fetchChildren();
  }, [user, childrenProp]);

  const handleEdit = (child, e) => {
    e.stopPropagation();
    setSelectedChild(child);
    setIsEditing(true);
    setOpenModal(true);
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  const showNotification = (message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  const handleDeleteClick = (child, e) => {
    e.stopPropagation();
    setDeleteDialog({
      open: true,
      child
    });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({
      open: false,
      child: null
    });
  };

  const handleDelete = async () => {
    const child = deleteDialog.child;
    try {
      // Call the delete function
      await HijosData.delete({
        padcodigo: child.padcodigo,
        hicodigo: child.hicodigo
      });

      // Remove the child from the local state
      setRegisteredChildren(prevChildren => 
        prevChildren.filter(c => !(c.padcodigo === child.padcodigo && c.hicodigo === child.hicodigo))
      );
      if (typeof onUpdate === 'function') {
        onUpdate();
      }

      // Show success notification
      showNotification('Jugador eliminado exitosamente', 'success');
    } catch (error) {
      console.error('Error deleting child:', error);
      showNotification('Error al eliminar el jugador: ' + error.message, 'error');
    } finally {
      handleDeleteCancel();
    }
  };

  const handleCardClick = (child) => {
    setSelectedChild(child);
    setIsEditing(false);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedChild(null);
    setIsEditing(false);
  };

  const handleAddPlayer = () => {
    setSelectedChild({
      padcodigo: userPadcodigo,
      nombre: '',
      apellidoPaterno: '',
      apellidoMaterno: '',
      club: '99', // Default to 'Por Definir'
      rama: 'V', // Default to 'Varonil'
      fnacimiento: new Date().toISOString().split('T')[0],
      curp: ''
    });
    setIsEditing(true);
    setOpenModal(true);
  };

  const handleSave = async (savedChild) => {
    try {
      // Refresh the children list after saving (cache already invalidated by create/update)
      const childrenData = await HijosData.fetch(user.email);
      setRegisteredChildren(childrenData);
      if (typeof onUpdate === 'function') {
        onUpdate();
      }
      
      // Show success notification
      showNotification('Jugador guardado exitosamente', 'success');
      
      // Close the modal
      handleCloseModal();
    } catch (error) {
      console.error('Error refreshing children data:', error);
      showNotification('Error al actualizar la lista de jugadores', 'error');
    }
  };

  const handleGiraInscription = (gira) => {
    if (isInscriptionClosed(gira?.insfin)) return;
    setSelectedGira(gira);
    setOpenGiraModal(true);
  };

  const handleCloseGiraModal = () => {
    setOpenGiraModal(false);
    setSelectedGira(null);
  };

  const handleSaveGiraInscription = async (registrationData) => {
    if (isInscriptionClosed(selectedGira?.insfin)) {
      showNotification('Inscripciones cerradas', 'error');
      return;
    }

    const getImporte = async (hicodigo, categoria) => {
      let importeValue;
      
      //Check for eventual then 7 y menores then 8-9 if not go by hijo 1,2,3
      if (registrationData.isEventual) {
        importeValue = selectedGira.importeeventual;
      } else if ([1, 2].includes(categoria)) {
        importeValue = selectedGira.importe4;
      } else if ([3, 4].includes(categoria)) {
        importeValue = selectedGira.importe8;
      } else {
        // For categories above 4, determine fee based on registration order
        // Exclude eventual registrations and registrations for the same child
        const registeredInHigherCategories = currentRegistrations?.filter(reg => 
          reg.inscategoria > 4 && 
          reg.inshijo !== hicodigo && 
          reg.instipo !== 3 // 3 = eventual registration
        ) || [];
        
        const registrationOrder = registeredInHigherCategories.length + 1; // +1 because this is the current registration
        
        switch (registrationOrder) {
          case 1: importeValue = selectedGira.importe1; break;
          case 2: importeValue = selectedGira.importe2; break;
          case 3: importeValue = selectedGira.importe3; break;
          default: importeValue = selectedGira.importe3; break; // After 3rd child, all get importe3
        }
      }

      // Check club association / recargo Academias (club 32 → clubFax) and adjust importe
      try {
        const response = await fetch(`${API_BASE_URL}/api/clubes`);
        if (response.ok) {
          const clubesData = await response.json();
          const club = clubesData.clubes.find(c => c.clubcodigo === registrationData.clubcodigo);
          
          if (club && club.clubasociado === 0) {
            importeValue = importeValue * 15;
          }

          // Academias Clubes Zona Sur (32): tarifa afiliado + recargo desde clubFax
          if (Number(registrationData.clubcodigo) === 32) {
            const recargoAcademia = Number(club?.clubfax);
            if (Number.isFinite(recargoAcademia) && recargoAcademia > 0) {
              importeValue = Number(importeValue) + recargoAcademia;
            }
          }
        }
      } catch (error) {
        console.error('Error fetching club data:', error);
        // Continue with original importe if club data fetch fails
      }

      return importeValue;
    };

    const getBancaria = (padcodigo, hicodigo) => {
      const banco = bancoLectura?.toString().padStart(3, '0').slice(-3) || '000';
      const jugador = `${padcodigo}${hicodigo}`.padStart(5, '0').slice(-5);
      const first8 = `${banco}${jugador}`;
      // Split first8 into individual digits
      const digits = first8.split('').map(digit => parseInt(digit, 10));
      const mult = [11, 13, 17, 19, 23, 29, 31, 37];
      // Multiply each digit by its corresponding multiplier and sum them
      let sum = 0;
      for (let i = 0; i < digits.length; i++) {
        sum += digits[i] * mult[i];
      }
      sum = sum + 498 + 524;
      const mod97 = (sum % 97);
      const valdigits = (99-mod97).toString().padStart(2, '0');
      return `${first8}${valdigits}`;
    };

    const getWagrImporte = (isWagr) => {
      return isWagr ? selectedGira.importewagr : 0;
    };

    const getCijImporte = (isCIJ) => {
      return isCIJ ? selectedGira.importecij : 0;
    };

    const getCurrentEtapa = () => {
      // Prefer etapa resuelta en el modal (SQL + Appwrite)
      if (registrationData.etapa != null && Number(registrationData.etapa) > 0) {
        return Number(registrationData.etapa);
      }
      if (!registrationData.isEventual) return 0;
      return getNextUpcomingEtapaCodigo(selectedGira?.etapas);
    };

    const currentDateTime = new Date().toISOString();

    const eventualValue = (eventual) => {
      let valorEventual;
      if (eventual === true) valorEventual = 3;
      else valorEventual = 1;

      return valorEventual; 
    };
    
    // Calculate importe asynchronously
    const calculatedImporte = await getImporte(registrationData.hicodigo, registrationData.categoriaId);
    
    const inscriptionData = {
      inspadre: registrationData.padcodigo,
      inshijo: registrationData.hicodigo,
      instipo: eventualValue(registrationData.isEventual),
      insedad: registrationData.edad,
      inscategoria: registrationData.categoriaId,
      insgira: registrationData.giracodigo,
      insetapa: getCurrentEtapa(),
      insimporte: calculatedImporte,
      insimpcnij: getCijImporte(registrationData.isCIJ),
      insreferencia: getBancaria(registrationData.padcodigo, registrationData.hicodigo),
      insmarcaimpreso: 0,
      insmarcapago: 0,
      insconfirmado: 0,
      insfecha: currentDateTime,
      insjugador: registrationData.hijugador,
      insmarcaupload: 0,
      insimagenpago: 0x00,
      insmarcaaviso: 0,
      insfechaaviso: currentDateTime,
      insfechaprocesopago: '1753-01-01 00:00:00.000',
      insobservacionpadre: '',
      insnombrep: userData ? `${userData.padnombre?.trim()} ${userData.padappaterno?.trim()} ${userData.padapmaterno?.trim()}`.trim() : '',
      insnombreh: `${registrationData.hinombre}`,
      instextobanco: '',
      insfechapagoaviso: '1753-01-01 00:00:00.000',
      insimporteinformado: 0,
      insimagenpago2: 0x00,
      insimportemjga: 0,
      insmarcacnijfmg: 0,
      insmarcapagofmg: 0,
      insobservainscripcion: '',
      insimportewagr: getWagrImporte(registrationData.isWAGR),
      // 0 = menores de 10 / cat 1-4; 1 = Premier; 2 = Campeonato
      insmarcadivision: (registrationData.edad != null && registrationData.edad < 10) ||
        [1, 2, 3, 4].includes(registrationData.categoriaId)
        ? 0
        : registrationData.division,
      insdesclub: clubes.find(club => club.id === registrationData.clubcodigo)?.nombre || '',
      inscodclub: registrationData.clubcodigo
    };
    //console.log(inscriptionData);
    
    // Prevent multiple submissions
    if (isSavingInscription) {
      return;
    }
    
    setIsSavingInscription(true);
    
    // Save the inscription data
    try {
      await GiraInfIns.save(inscriptionData);

      if (registrationData.isEventual && Number(inscriptionData.insetapa) > 0) {
        try {
          await incrementEventInsForEtapaInfantil(
            registrationData.giracodigo,
            inscriptionData.insetapa
          );
        } catch (e) {
          console.error('No se pudo actualizar cupo de eventuales (Appwrite):', e);
          showNotification(
            `Inscripción guardada, pero no se actualizó eventIns: ${e.message || e}`,
            'warning'
          );
          // Refresh anyway and exit early so we don't overwrite with a success-only toast
          const data = await GiraInfIns.fetch(userPadcodigo, selectedGira.gira);
          setCurrentRegistrations(data);
          return;
        }
      }

      showNotification('Inscripción guardada exitosamente', 'success');
      // Refresh the registrations list
      const data = await GiraInfIns.fetch(userPadcodigo, selectedGira.gira);
      setCurrentRegistrations(data);
    } catch (error) {
      console.error('Error saving inscription:', error);
      showNotification('Error al guardar la inscripción: ' + error.message, 'error');
    } finally {
      setIsSavingInscription(false);
      handleCloseGiraModal();
    }
  };

  // Fetch registrations when userPadcodigo and selectedGira are available
  useEffect(() => {
    const fetchRegistrations = async () => {
      if (!userPadcodigo || !selectedGira) return;

      try {
        const data = await GiraInfIns.fetch(userPadcodigo, selectedGira.gira);
        setCurrentRegistrations(data);
      } catch (error) {
        console.error('Error fetching registrations:', error);
        setCurrentRegistrations([]);
      }
    };

    fetchRegistrations();
  }, [userPadcodigo, selectedGira]);

  useEffect(() => {
    const fetchBancoLectura = async () => {
      const bancos = await GiraInfIns.getBancoLectura();
      if (bancos.length > 0) setBancoLectura(bancos[0].BancoCodigoLectura);
    };
    fetchBancoLectura();
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Registro Hijos */}
      <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 3, boxShadow: 1 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 3 
        }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Registro de Niños/as
          </Typography>
          <Button
            variant="contained"
            startIcon={isLoadingClubes ? <CircularProgress size={20} /> : <AddIcon />}
            onClick={handleAddPlayer}
            disabled={isLoadingClubes}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3
            }}
          >
            {isLoadingClubes ? 'Cargando...' : 'Agregar Jugador'}
          </Button>
        </Box>

        {isLoadingChildren ? (
          <Box sx={{ 
            textAlign: 'center', 
            py: 4,
            bgcolor: 'background.default',
            borderRadius: 2
          }}>
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography color="text.secondary">
              Cargando jugadores...
            </Typography>
          </Box>
        ) : registeredChildren.length > 0 ? (
                     <Grid container spacing={3}>
             {registeredChildren.map((child, index) => (
               <Grid item xs={12} sm={6} md={12} lg={6} key={index}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    boxShadow: 1,
                    '&:hover': {
                      boxShadow: 3,
                      cursor: 'pointer'
                    },
                    position: 'relative'
                  }}
                  onClick={() => handleCardClick(child)}
                >
                  <Typography variant="h6" sx={{ mb: 1, pr: { md: 8 } }}>
                    {`${child.nombre} ${child.apellidoPaterno} ${child.apellidoMaterno}`}
                  </Typography>
                  <Box sx={{ mt: 2, mb: { xs: 6, sm: 6, md: 0 } }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {`${calculateAge(child.fnacimiento)} años • ${getCategoria(child.rama)}`}
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    position: { xs: 'absolute', md: 'static' },
                    bottom: { xs: 16, md: 'auto' },
                    right: { xs: 16, md: 'auto' },
                    display: 'flex',
                    gap: 1,
                    flexWrap: 'wrap',
                    justifyContent: { xs: 'flex-end', md: 'flex-start' },
                    maxWidth: { xs: 'calc(100% - 32px)', md: '100%' },
                    mt: { xs: 0, md: 2 }
                  }}>
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      size="small"
                      onClick={(e) => handleEdit(child, e)}
                      sx={{
                        textTransform: 'none',
                        borderRadius: 2,
                        fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
                        minWidth: { xs: '60px', sm: 'auto', md: '80px' },
                        px: { xs: 1, sm: 2, md: 2 },
                        height: { xs: '32px', sm: '32px', md: '36px' }
                      }}
                    >
                      <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                        Editar
                      </Box>
                      <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                        Ed
                      </Box>
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      size="small"
                      onClick={(e) => handleDeleteClick(child, e)}
                      sx={{
                        textTransform: 'none',
                        borderRadius: 2,
                        fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
                        minWidth: { xs: '60px', sm: 'auto', md: '80px' },
                        px: { xs: 1, sm: 2, md: 2 },
                        height: { xs: '32px', sm: '32px', md: '36px' }
                      }}
                    >
                      <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                        Eliminar
                      </Box>
                      <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                        Del
                      </Box>
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ 
            textAlign: 'center', 
            py: 4,
            bgcolor: 'background.default',
            borderRadius: 2
          }}>
            <Typography color="text.secondary">
              No hay jugadores registrados
            </Typography>
          </Box>
        )}
      </Box>

      {/* Registro Giras */}
      <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 3, boxShadow: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Registro de Giras
        </Typography>

        {isLoadingGiras ? (
          <Box sx={{ 
            textAlign: 'center', 
            py: 4,
            bgcolor: 'background.default',
            borderRadius: 2,
            mt: 2
          }}>
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography color="text.secondary">
              Cargando giras...
            </Typography>
          </Box>
        ) : !Array.isArray(giras) || giras.length === 0 ? (
          <Box sx={{ 
            textAlign: 'center', 
            py: 4,
            bgcolor: 'background.default',
            borderRadius: 2,
            mt: 2
          }}>
            <Typography color="text.secondary">
              No hay giras vigentes
            </Typography>
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            {giras.map((gira, index) => (
              <Box 
                key={index}
                sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  p: 2,
                  bgcolor: 'background.default',
                  borderRadius: 2,
                  mb: 2
                }}
              >
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  justifyContent: 'space-between'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      component="img"
                      src={gira.foto}
                      alt={`Logo ${gira.nombre}`}
                      sx={{
                        width: 110,
                        height: 110,
                        objectFit: 'contain',
                        borderRadius: 3,
                        display: { xs: 'none', sm: 'block' }
                      }}
                    />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {gira.nombre}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {`${formatDate(gira.inicio)} - ${formatDate(gira.fin)}`}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleGiraInscription(gira)}
                    disabled={isInscriptionClosed(gira.insfin) || areAllPlayersRegistered(gira.gira, gira.etapas)}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      px: 3,
                      bgcolor: (isInscriptionClosed(gira.insfin) || areAllPlayersRegistered(gira.gira, gira.etapas)) ? 'grey.400' : 'primary.main',
                      '&:hover': {
                        bgcolor: (isInscriptionClosed(gira.insfin) || areAllPlayersRegistered(gira.gira, gira.etapas)) ? 'grey.400' : 'primary.dark'
                      }
                    }}
                  >
                    {isInscriptionClosed(gira.insfin)
                      ? 'Inscripciones Cerradas'
                      : areAllPlayersRegistered(gira.gira, gira.etapas)
                        ? 'Todos Inscritos'
                        : 'Inscribirse'}
                  </Button>
                </Box>

                {/* Current Registrations */}
                {(() => {
                  const giraRegistrations = (Array.isArray(currentRegistrations) ? currentRegistrations : [])
                    .filter(reg => Number(reg.insgira) === Number(gira.gira));
                  return giraRegistrations.length > 0 ? (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Registros Actuales
                    </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: 1,
                      pl: 2
                    }}>
                      {giraRegistrations.map((reg, idx) => (
                        <Box 
                          key={idx}
                          sx={{ 
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: 2,
                            alignItems: { xs: 'flex-start', sm: 'center' }
                          }}
                        >
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: { xs: 0.5, sm: 2 },
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            flex: 1
                          }}>
                            <Typography variant="body2">
                              {reg.insnombreh}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Edad: {reg.insedad}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Ref: {reg.insreferencia}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {Number(reg.insetapa) === 0
                                ? 'Gira completa'
                                : `Etapa: ${reg.insetapa != null ? reg.insetapa : '—'}`}
                            </Typography>
                          </Box>
                          <Box sx={{ 
                            display: 'flex', 
                            gap: 1, 
                            ml: { xs: 0, sm: 'auto' },
                            flexWrap: 'wrap',
                            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                            maxWidth: '100%',
                            width: { xs: '100%', sm: 'auto' }
                          }}>
                            <Button
                              variant="outlined"
                              size="small"
                              disabled={generatingJustificante}
                              sx={{
                                textTransform: 'none',
                                borderRadius: 2,
                                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                minWidth: { xs: '120px', sm: '150px' },
                                maxWidth: { xs: '140px', sm: '150px' },
                                px: { xs: 1, sm: 2 },
                                position: 'relative',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                              onClick={async () => {
                                if (generatingJustificante) return;
                                setGeneratingJustificante(true);
                                const encargado = await agsRoles('social');
                                try {
                                  const pdfBlob = await JustificanteEscolar({
                                    alumno: reg.insnombreh,
                                    etapas: defineEtapas(gira.etapas, reg),
                                    gira: gira.nombre,
                                    nombre: encargado.nombre,
                                    firma: encargado.firma,
                                    titulo: encargado.titulo,
                                  });
                                  
                                  const url = window.URL.createObjectURL(pdfBlob);
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.download = `justificante_${reg.insnombreh}.pdf`;
                                  
                                  document.body.appendChild(link);
                                  link.click();
                                  
                                  document.body.removeChild(link);
                                  window.URL.revokeObjectURL(url);
                                } catch (error) {
                                  console.error('Error generating PDF:', error);
                                  showNotification('Error al generar el PDF', 'error');
                                } finally {
                                  setGeneratingJustificante(false);
                                }
                              }}
                            >
                              {generatingJustificante ? (
                                <CircularProgress size={20} sx={{ color: 'inherit' }} />
                              ) : (
                                <Box sx={{ 
                                  display: { xs: 'none', sm: 'block' },
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}>
                                  Justificante Escolar
                                </Box>
                              )}
                              {generatingJustificante ? null : (
                                <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                                  Justificante
                                </Box>
                              )}
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              disabled={generatingFicha}
                              sx={{
                                textTransform: 'none',
                                borderRadius: 2,
                                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                minWidth: { xs: '120px', sm: '150px' },
                                maxWidth: { xs: '140px', sm: '150px' },
                                px: { xs: 1, sm: 2 },
                                position: 'relative',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                              onClick={async () => {
                                if (generatingFicha) return;
                                setGeneratingFicha(true);
                                try {
                                  const pdfBlob = await FichaDeposito({
                                    jugador: reg.insnombreh,
                                    gira: gira.nombre,
                                    jugadorRef: reg.insreferencia,
                                    giraImporte: reg.insimporte,
                                    wagrImporte: reg.insimportewagr,
                                    cijImporte: reg.insimpcnij,
                                    banco: 'BANAMEX',
                                    cuenta: '39545',
                                    sucursal: '4417',
                                    clabe: '002542441700395457',
                                    beneficiario: 'Asociación de Golf del Sur A.C.'
                                  });
                                  
                                  const url = window.URL.createObjectURL(pdfBlob);
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.download = `ficha_deposito_${reg.insnombreh}.pdf`;
                                  
                                  document.body.appendChild(link);
                                  link.click();
                                  
                                  document.body.removeChild(link);
                                  window.URL.revokeObjectURL(url);
                                } catch (error) {
                                  console.error('Error generating PDF:', error);
                                  showNotification('Error al generar el PDF', 'error');
                                } finally {
                                  setGeneratingFicha(false);
                                }
                              }}
                            >
                              {generatingFicha ? (
                                <CircularProgress size={20} sx={{ color: 'inherit' }} />
                              ) : (
                                <Box sx={{ 
                                  display: { xs: 'none', sm: 'block' },
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}>
                                  Ficha de Deposito
                                </Box>
                              )}
                              {generatingFicha ? null : (
                                <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                                  Ficha
                                </Box>
                              )}
                            </Button>
                            {reg.instipo !== 3 && (
                              <Button
                                variant="outlined"
                                size="small"
                                disabled={generatingCredencial}
                                sx={{
                                  textTransform: 'none',
                                  borderRadius: 2,
                                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                  minWidth: { xs: '120px', sm: '150px' },
                                  maxWidth: { xs: '140px', sm: '150px' },
                                  px: { xs: 1, sm: 2 },
                                  position: 'relative',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}
                                onClick={async () => {
                                  if (generatingCredencial) return;
                                  setGeneratingCredencial(true);
                                  try {
                                  const jugadorClean = (reg.insnombreh || '').trim().replace(/\s+/g, ' ');
                                    const clubName = reg.insdesclub || clubes.find(c => c.id === reg.inscodclub)?.nombre || '';
                                    const validezDate = gira.giraffinalizacion ?? gira.fin;
                                    const validezStr = validezDate ? formatDate(validezDate) : '';
                                    // En local usa URL pública de producción; en deploy usa el origin del sitio (o REACT_APP_PUBLIC_URL).
                                    const baseUrl = process.env.REACT_APP_PUBLIC_URL
                                      || (typeof window !== 'undefined'
                                        && window.location.hostname
                                        && !['localhost', '127.0.0.1'].includes(window.location.hostname)
                                          ? window.location.origin
                                          : 'https://giras.zonasur.org.mx');
                                    const pdfBlob = await CredencialGira({
                                      jugador: jugadorClean,
                                      club: clubName,
                                      gira: gira.nombre,
                                      validez: validezStr,
                                      referencia: String(reg.insreferencia ?? '').trim(),
                                      baseUrl
                                    });

                                    const url = window.URL.createObjectURL(pdfBlob);
                                    const link = document.createElement('a');
                                    link.href = url;
                                  const safeJugadorForFile = jugadorClean
                                    .replace(/\s+/g, '_')
                                    .replace(/[<>:"/\\|?*]/g, '');
                                  link.download = `credencial_${safeJugadorForFile || 'jugador'}.pdf`;

                                    document.body.appendChild(link);
                                    link.click();

                                    document.body.removeChild(link);
                                    window.URL.revokeObjectURL(url);
                                  } catch (error) {
                                    console.error('Error generating credential PDF:', error);
                                    showNotification('Error al generar la credencial', 'error');
                                  } finally {
                                    setGeneratingCredencial(false);
                                  }
                                }}
                              >
                                {generatingCredencial ? (
                                  <CircularProgress size={20} sx={{ color: 'inherit' }} />
                                ) : (
                                  <Box sx={{
                                    display: { xs: 'none', sm: 'block' },
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}>
                                    Credencial
                                  </Box>
                                )}
                                {generatingCredencial ? null : (
                                  <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                                    Credencial
                                  </Box>
                                )}
                              </Button>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  ) : null;
                })()}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Add Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">
          Confirmar eliminación
        </DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro que deseas eliminar a {deleteDialog.child?.nombre} {deleteDialog.child?.apellidoPaterno}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

             <ChildRegistrationModal
         open={openModal}
         onClose={handleCloseModal}
         selectedChild={selectedChild}
         isEditing={isEditing}
         clubes={clubes}
         onSave={handleSave}
         userPadcodigo={userPadcodigo}
         user={user}
       />

              <GiraInfRegistration
          open={openGiraModal}
          onClose={handleCloseGiraModal}
          selectedGira={selectedGira}
          registeredChildren={registeredChildren}
          onSave={handleSaveGiraInscription}
          userPadcodigo={userPadcodigo}
          currentRegistrations={currentRegistrations}
          isSaving={isSavingInscription}
        />
    </Box>
  );
};

export default Infantiles;
